import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { createCoupon } from '../../api/coupons';
import { CouponCreateRequest } from '../../types/coupon';
import { formatDateToISO } from '../../utils/dateUtils';
import CouponImmediateStartConfirmDialog from './CouponImmediateStartConfirmDialog';

interface CouponRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FieldErrors {
  code?: string;
  name?: string;
  discount_value?: string;
  start_date?: string;
  end_date?: string;
  usage_limit?: string;
}

const CouponRegistrationDialog: React.FC<CouponRegistrationDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ errors, setErrors ] = useState<string[]>([]);
  const [ fieldErrors, setFieldErrors ] = useState<FieldErrors>({});
  const [ showImmediateStartConfirm, setShowImmediateStartConfirm ] = useState(false);
  // フォームの状態
  const [ formData, setFormData ] = useState<CouponCreateRequest>({
    code: '',
    name: '',
    discount_type: 'FIXED',
    discount_value: '' as any,
    start_date: formatDateToISO(new Date()),
    end_date: '',
    usage_limit: null,
    remarks: '',
  });
  // ダイアログが開いた時の初期化
  useEffect(() => {
    if (open) {
      setFormData({
        code: '',
        name: '',
        discount_type: 'FIXED',
        discount_value: '' as any,
        start_date: formatDateToISO(new Date()),
        end_date: '',
        usage_limit: null,
        remarks: '',
      });
      setError(null);
      setErrors([]);
      setFieldErrors({});
      setShowImmediateStartConfirm(false);
    }
  }, [ open ]);
  // フォームの値を更新
  const handleChange = (field: keyof CouponCreateRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'discount_value'
        ? value === '' ? '' : Number(value)
        : field === 'usage_limit'
          ? value === '' ? null : Number(value)
          : value
    }));
    // フィールドのエラーをクリア
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors((prev: FieldErrors) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  // バリデーション
  const validate = (): boolean => {
    const newErrors: string[] = [];
    const newFieldErrors: FieldErrors = {};
    // クーポンコードのバリデーション
    if (!formData.code || formData.code.length < 4 || formData.code.length > 10) {
      newFieldErrors.code = 'クーポンコードは4〜10文字で入力してください';
      newErrors.push(newFieldErrors.code);
    } else if (formData.code && !/^[A-Za-z0-9]+$/.test(formData.code)) {
      newFieldErrors.code = 'クーポンコードは英数字のみ使用できます';
      newErrors.push(newFieldErrors.code);
    }
    // クーポン名のバリデーション
    if (!formData.name) {
      newFieldErrors.name = 'クーポン名を入力してください';
      newErrors.push(newFieldErrors.name);
    } else if (formData.name && formData.name.length > 20) {
      newFieldErrors.name = 'クーポン名は20文字以内で入力してください';
      newErrors.push(newFieldErrors.name);
    }
    // 割引値のバリデーション
    if (formData.discount_type === 'RATE' && (formData.discount_value < 1 || formData.discount_value > 100)) {
      newFieldErrors.discount_value = 'パーセント割引の場合、割引値は1〜100の範囲で入力してください';
      newErrors.push(newFieldErrors.discount_value);
    } else if (formData.discount_value < 1) {
      newFieldErrors.discount_value = '割引値は1以上で入力してください';
      newErrors.push(newFieldErrors.discount_value);
    }
    // 日付のバリデーション
    if (!formData.start_date) {
      newFieldErrors.start_date = '開始日を入力してください';
      newErrors.push(newFieldErrors.start_date);
    }
    // end_dateが入力されている場合のみ、開始日との比較をチェック
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      newFieldErrors.end_date = '終了日は開始日より後の日付を入力してください';
      newErrors.push(newFieldErrors.end_date);
    }
    // 利用回数上限のバリデーション（任意入力）
    if (formData.usage_limit !== null && formData.usage_limit !== undefined && formData.usage_limit < 1) {
      newFieldErrors.usage_limit = '利用上限は１以上を入力してください';
      newErrors.push(newFieldErrors.usage_limit);
    }
    setErrors(newErrors);
    setFieldErrors(newFieldErrors);
    return newErrors.length === 0;
  };
  // 送信処理
  const handleSubmit = async() => {
    if (!validate()) {
      return;
    }
    // 本日開始の場合は確認ダイアログを表示
    const today = formatDateToISO(new Date());
    if (formData.start_date === today && !showImmediateStartConfirm) {
      setShowImmediateStartConfirm(true);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setErrors([]);
    try {
      await createCoupon({
        ...formData,
        end_date: formData.end_date || undefined,
        usage_limit: formData.usage_limit ?? undefined,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('クーポン作成エラー:', error);
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        setErrors(serverErrors);
        // サーバーエラーをフィールドにマッピング
        const mappedFieldErrors: FieldErrors = {};
        serverErrors.forEach((errMsg: string) => {
          if (errMsg.includes('クーポンコード')) {
            mappedFieldErrors.code = errMsg;
          } else if (errMsg.includes('クーポン名')) {
            mappedFieldErrors.name = errMsg;
          } else if (errMsg.includes('割引値')) {
            mappedFieldErrors.discount_value = errMsg;
          } else if (errMsg.includes('開始日')) {
            mappedFieldErrors.start_date = errMsg;
          } else if (errMsg.includes('終了日')) {
            mappedFieldErrors.end_date = errMsg;
          } else if (errMsg.includes('利用回数') || errMsg.includes('利用回数')) {
            mappedFieldErrors.usage_limit = errMsg;
          }
        });
        setFieldErrors(mappedFieldErrors);
      } else {
        setError('クーポンの作成に失敗しました。再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
      setShowImmediateStartConfirm(false);
    }
  };
  // 確認ダイアログからの続行処理
  const handleConfirmImmediateStart = () => {
    setShowImmediateStartConfirm(false);
    handleSubmit();
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={(_event: React.SyntheticEvent, reason?: string) => {
          // backdropClickやescapeKeyDownによる閉じるを無効化
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            onClose();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>クーポン登録</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {(error || errors.length > 0) && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {error && <div>{error}</div>}
                  {errors.map((err: string, index: number) => (
                    <div key={index}>{err}</div>
                  ))}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="クーポン名"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange('name')}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || "最大20文字"}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="クーポンコード"
                fullWidth
                required
                value={formData.code}
                onChange={handleChange('code')}
                error={!!fieldErrors.code}
                helperText={fieldErrors.code || "4〜10文字の半角英数字（A-Z, a-z, 0-9）"}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>割引タイプ</InputLabel>
                <Select
                  value={formData.discount_type}
                  onChange={handleChange('discount_type')}
                  label="割引タイプ"
                >
                  <MenuItem value="FIXED">固定額</MenuItem>
                  <MenuItem value="RATE">パーセント</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="割引値"
                type="number"
                fullWidth
                required
                value={formData.discount_value ?? ''}
                onChange={handleChange('discount_value')}
                error={!!fieldErrors.discount_value}
                inputProps={{ min: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {formData.discount_type === 'FIXED' ? '円' : '%'}
                    </InputAdornment>
                  ),
                }}
                helperText={
                  fieldErrors.discount_value ||
                  (formData.discount_type === 'RATE'
                    ? '１〜１００%'
                    : '１円以上')
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="開始日"
                type="date"
                fullWidth
                required
                value={formData.start_date}
                onChange={handleChange('start_date')}
                error={!!fieldErrors.start_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: formatDateToISO(new Date())
                }}
                helperText={fieldErrors.start_date || "本日以降の日付"}
              />
            </Grid>

            <Grid item xs={6}>
              <Box position="relative">
                <TextField
                  label="終了日（任意）"
                  type="date"
                  fullWidth
                  value={formData.end_date}
                  onChange={handleChange('end_date')}
                  error={!!fieldErrors.end_date}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: formatDateToISO(new Date()),
                  }}
                  helperText={fieldErrors.end_date || "未入力の場合は無期限となります"}
                />
                {!formData.end_date && (
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: '40px',  // 右側に配置（カレンダーアイコンの左）
                      transform: 'translateY(-50%)',
                      color: 'text.secondary',
                      pointerEvents: 'none',
                      fontSize: '0.875rem',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                    }}
                  >
                    期限なし
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box position="relative">
                <TextField
                  label="利用上限（任意）"
                  type="number"
                  fullWidth
                  value={formData.usage_limit ?? ''}
                  onChange={handleChange('usage_limit')}
                  error={!!fieldErrors.usage_limit}
                  helperText={fieldErrors.usage_limit || "未入力の場合は無制限となります"}
                  inputProps={{ min: 1 }}
                />
                {(formData.usage_limit === null || formData.usage_limit === undefined || formData.usage_limit === 0) && (
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: '14px',
                      transform: 'translateY(-50%)',
                      color: 'text.secondary',
                      pointerEvents: 'none',
                      fontSize: '0.875rem',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                    }}
                  >
                    無制限
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="備考"
                fullWidth
                multiline
                rows={3}
                value={formData.remarks}
                onChange={handleChange('remarks')}
                helperText="任意"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? '登録中...' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 即日開始確認ダイアログ */}
      <CouponImmediateStartConfirmDialog
        open={showImmediateStartConfirm}
        onClose={() => setShowImmediateStartConfirm(false)}
        onConfirm={handleConfirmImmediateStart}
        mode="register"
      />
    </>
  );
};
export default CouponRegistrationDialog;
